<template>
  <div
    @click="handleCardClick"
    @click.middle="handleCardClick"
    @keyup.enter="handleCardEnter"
    class="rounded-0 card"
    :class="cardClass"
    :style="cardStyle"
    :tabindex="cardTabIndex">
    <div class="row gx-0 h-100 overflow-hidden cla-card">
      <div class="col-5 cla-bg-image-wrapper overflow-hidden d-flex">
        <div
          class="cla-bg-image w-100"
          :class="{ 'cla-lazy': bgImageLazy }"
          :data-background-image="dataBgImage"
          :style="bgImage"></div>
      </div>
      <div class="col-7 flex-grow-1">
        <div
          class="px-space-sm py-space-md h-100 cla-highlight d-flex align-items-center"
          :class="cardBodyClass"
          :style="cardBodyStyle">
          <component :is="titleLevel" :class="titleClass">{{
            title
          }}</component>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";

interface Props {
  isClickable?: boolean;
  displayBorder?: boolean;
  borderVariant?: string;
  bgImageLazy?: boolean;
  imageSource: string;
  displayHighlight?: boolean;
  highlightColor?: string;
  highlightWidth?: number;
  bodyBgVariant?: string;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: string;
  titleVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isClickable: false,
  displayBorder: true,
  borderVariant: "light-2",
  bgImageLazy: true,
  displayHighlight: true,
  highlightColor: "var( --rds-secondary, #ffc627)",
  highlightWidth: 8,
  bodyBgVariant: "light-2",
  titleLevel: "h3",
  titleSize: "medium",
  titleVariant: "dark-3",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "cardClick", event: Event): void;
  /**
   *  Emitted when any the enter key is pressed while the card has focus and is-clickable is set to true.
   */
  (e: "cardEnter", event: Event): void;
}>();

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

const cardTabIndex = computed(() => {
  return props.isClickable ? "0" : "-1";
});

const cardClass = computed(() => {
  let classNames = props.displayBorder
    ? `border-${props.borderVariant}`
    : `border-0`;

  if (props.isClickable) {
    classNames += " card-focus";
  }

  return classNames;
});

const cardStyle = computed(() => {
  return { cursor: props.isClickable ? "pointer" : "default" };
});

const bgImage = computed(() => {
  if (!props.bgImageLazy) {
    return { "background-image": `url('${props.imageSource}')` };
  } else {
    return null;
  }
});

const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.imageSource : null;
});

const cardBodyClass = computed(() => {
  return `bg-${props.bodyBgVariant}`;
});

const cardBodyStyle = computed(() => {
  if (props.displayHighlight) {
    return {
      "border-left": `${props.highlightWidth}px solid ${getColor(
        props.highlightColor
      )} !important`,
      "background-image": `linear-gradient(${getColor(
        props.highlightColor
      )}, ${getColor(props.highlightColor)})`,
    };
  } else {
    return null;
  }
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const handleCardClick = (event: Event) => {
  emits("cardClick", event);
};
const handleCardEnter = (event: Event) => {
  if (props.isClickable) {
    emits("cardEnter", event);
  }
};
onMounted(() => {
  if (typeof window !== "undefined" && window !== null && props.bgImageLazy) {
    const observer = lozad(".cla-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.cla-bg-image {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
  transition: transform 0.5s ease;
}

.cla-highlight {
  background-repeat: no-repeat;
  background-size: 0% 100%;
  background-position: left top;
  transition: background-size 0.5s ease;
}

.cla-card:hover .cla-bg-image {
  transform: scale(1.1);
}

.cla-card:hover .cla-highlight {
  background-size: 100% 100%;
}
</style>
